import { buildObjectPoints, rotateVectorByQuaternion, transformPoint } from "./qr-pose.js";
import { GPS_POINT_SOURCE_SYNTHETIC_QR, calcGpsCoords } from "gps-plus-slam-js";
//#region ../src/ar/qr/qr-gps-vote.ts
/**
* `count` points evenly spaced on a circle of radius `baselineM` in the QR
* plane (z = 0), starting at local +x. The symmetric ring's centroid is the QR
* center, so translation stays anchored there while the wide radius stiffens
* the rotation fit. Non-collinear for `count ≥ 3`.
*/
function widePlanePoints(baselineM, count) {
	const points = [];
	for (let i = 0; i < count; i++) {
		const theta = 2 * Math.PI * i / count;
		points.push([
			baselineM * Math.cos(theta),
			baselineM * Math.sin(theta),
			0
		]);
	}
	return points;
}
/**
* Map a QR-local plane offset (meters; +x right, +y up on the printed face) to
* an East/North/Up offset, given the QR's compass heading. Vertical-QR
* convention: +y is world up; +x is horizontal at `headingDeg` (clockwise from
* North), so east = x·sin(h), north = x·cos(h).
*/
function localPlaneToEnu(localX, localY, headingDeg) {
	const h = headingDeg * Math.PI / 180;
	return {
		east: localX * Math.sin(h),
		north: localX * Math.cos(h),
		up: localY
	};
}
/**
* Map a QR-local point (meters, `buildObjectPoints` axes) to an
* East/North/Up offset using whichever orientation the geo pose carries:
* the full `rotation` quaternion (6-DoF, wins when both are present) or the
* legacy vertical-poster `headingDeg`. A vertical-poster quaternion
* reproduces the heading path exactly (property-tested). Throws when the
* pose carries neither — that level should never have parsed.
*/
function localPlaneOffset(local, geo) {
	if (geo.rotation !== void 0) {
		const nue = rotateVectorByQuaternion(geo.rotation, local);
		return {
			north: nue[0],
			up: nue[1],
			east: nue[2]
		};
	}
	if (geo.headingDeg === void 0) throw new RangeError("qr-gps-vote: QrGeoPose carries neither headingDeg nor rotation");
	return localPlaneToEnu(local[0], local[1], geo.headingDeg);
}
/** Apply an ENU meter offset to a geo pose (equirectangular; exact enough for sub-meter QR corners). */
function offsetGeo(center, enu) {
	const nearPole = Math.abs(Math.cos(center.lat * Math.PI / 180)) < 1e-14;
	const { lat, lon } = calcGpsCoords({
		lat: center.lat,
		lon: center.lon
	}, [
		enu.north,
		enu.up,
		nearPole ? 0 : enu.east
	]);
	return {
		latitude: lat,
		longitude: lon,
		altitude: center.alt + enu.up
	};
}
/**
* Build the synthetic `recordGpsEvent` payload(s) for one QR detection. Returns
* 4 corner correspondences by default, or 1 center correspondence when
* `multiCorrespondence` is false. Each payload pairs the corner's odom position
* (from the solved pose) with its absolute geo position (from the level file),
* stamped with the tiny synthetic accuracy.
*
* @throws RangeError on a non-positive `sizeM` or `syntheticAccuracyM`.
*/
function buildQrGpsVotes(input) {
	const { qrPoseWorld, sizeM, qrGeo, syntheticAccuracyM, odomRotation = qrPoseWorld.rotation, multiCorrespondence = true, baselineM, count = 4, timestamp = Date.now(), idPrefix = "qr" } = input;
	if (!(syntheticAccuracyM > 0) || !Number.isFinite(syntheticAccuracyM)) throw new RangeError(`qr-gps-vote: syntheticAccuracyM must be positive, got ${syntheticAccuracyM}`);
	const useWideBaseline = baselineM !== void 0 && Number.isFinite(baselineM) && baselineM > 0;
	if (useWideBaseline && count < 3) throw new RangeError(`qr-gps-vote: wide-baseline count must be >= 3 (collinear otherwise), got ${count}`);
	return (useWideBaseline ? widePlanePoints(baselineM, count) : multiCorrespondence ? buildObjectPoints(sizeM) : [[
		0,
		0,
		0
	]]).map((local, i) => {
		const odomPosition = transformPoint(local, qrPoseWorld);
		const geo = offsetGeo(qrGeo, localPlaneOffset(local, qrGeo));
		return {
			odomPosition,
			odomRotation,
			rawGpsPoint: {
				id: `${idPrefix}-${timestamp}-${i}`,
				latitude: geo.latitude,
				longitude: geo.longitude,
				altitude: geo.altitude,
				latLongAccuracy: syntheticAccuracyM,
				timestamp,
				source: GPS_POINT_SOURCE_SYNTHETIC_QR
			}
		};
	});
}
//#endregion
export { buildQrGpsVotes, localPlaneOffset, localPlaneToEnu, offsetGeo };
